// @noflow
import {
  getJson,
  postJson,
  post,
  del,
  paramify,
  appendParamsToQueryString,
} from '../utilities/fetchUtils';
import type { ThunkAction } from '../types';
import type { Card, PurchaseInfo, EstAuthorizedDevice, ViewContent } from './types';

// **** EXTERNAL API ACTIONS **** //

// GET /est/devices
export const fetchEstAuthorizedDevices = (): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();
  const {
    auth: { viewer: { uuid } = {} },
  } = state;

  if (!uuid) return undefined;

  dispatch({ type: 'REQUEST_EST_AUTHORIZED_DEVICES' });

  const url = `${config.session.radish_url}est/devices`;

  return getJson({ url, state })
    .then((payload: Array<EstAuthorizedDevice>) => {
      dispatch({ type: 'RECEIVE_EST_AUTHORIZED_DEVICES', payload });
    })
    .catch((error: Error) => {
      console.warn('fetch error', error);
      dispatch({ type: 'REQUEST_EST_AUTHORIZED_DEVICES_ERROR' });
    });
};

// POST /est/device
export const registerEstDevice = (
  device: EstAuthorizedDevice,
  callback?: (boolean, number) => void,
): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();
  const {
    auth: { viewer: { uuid } = {} },
  } = state;

  if (!uuid) return undefined;

  const headers = { Accept: 'application/vnd.echostar.timers+json;version=1' };
  const data = {
    device_id: device.device_id,
    device_name: device.name,
  };
  const url = `${config.session.radish_url}est/device`;

  dispatch({ type: 'REQUEST_REGISTER_EST_DEVICE' });

  return post({ url, state, data, headers })
    .then(response => {
      if (response.status >= 200 && response.status < 300) {
        dispatch({
          type: 'RECEIVE_REGISTER_EST_DEVICE',
          device: response,
        });
        if (callback) {
          callback(true, response.status);
        }
      } else {
        throw Error(response.status);
      }
    })
    .catch(error => {
      dispatch({
        type: 'RECEIVE_REGISTER_EST_DEVICE_ERROR',
      });
      console.warn('RECEIVE_REGISTER_EST_DEVICE_ERROR', error);
      if (callback) {
        callback(false, error.message);
      }
    });
};

// DELETE /est/device
export const removeEstDevice = (deviceId: string, callback?: boolean => void): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();
  const {
    auth: { viewer: { uuid } = {} },
  } = state;

  if (!uuid) return undefined;

  const headers = { Accept: 'application/vnd.echostar.timers+json;version=1' };
  const url = `${config.session.radish_url}est/device`;

  dispatch({ type: 'REQUEST_REMOVE_EST_DEVICE', payload: deviceId });

  return del({ url, state, data: { device_id: deviceId }, headers })
    .then(response => {
      if (response.status >= 200 && response.status < 300) {
        if (callback) {
          callback(true);
        }
        dispatch({ type: 'RECEIVE_REMOVE_EST_DEVICE', payload: deviceId });
      } else {
        if (callback) {
          callback(false);
        }
        dispatch({
          type: 'REQUEST_REMOVE_EST_DEVICE_ERROR',
          payload: {
            errorDescription: 'Error removing device',
            deviceId,
          },
        });
      }
    })
    .catch((error: Error) => {
      if (callback) {
        callback(false);
      }
      dispatch({
        type: 'REQUEST_REMOVE_EST_DEVICE_ERROR',
        payload: {
          errorDescription: 'Error removing device',
          deviceId,
        },
      });
      console.warn('REQUEST_REMOVE_EST_DEVICE_ERROR fetch error', error);
    });
};

// GET /est/product_context
export const fetchPricingInfo = (echoId: number): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();
  const {
    auth: { viewer: { uuid } = {} },
  } = state;

  if (!uuid) return undefined;

  dispatch({
    type: 'REQUEST_PRICING_INFO',
  });

  const url = `${config.session.radish_url}est/product_context?echostar3_id=${echoId}`;

  return getJson({ url, state, xhrFields: { withCredentials: true } })
    .then(payload => {
      dispatch({
        type: 'RECEIVE_PRICING_INFO',
        payload,
      });
    })
    .catch((error: Error) => {
      console.warn('fetch error', error);
      dispatch({
        type: 'REQUEST_PRICING_INFO_ERROR',
      });
    });
};

// GET /est/payment_info
export const fetchPaymentInfo = (): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();
  const {
    auth: { viewer: { uuid } = {} },
  } = state;

  if (!uuid) return undefined;

  dispatch({
    type: 'REQUEST_PAYMENT_INFO',
  });

  const url = `${config.session.radish_url}est/payment_info`;
  return getJson({ url, state })
    .then(payload => {
      dispatch({
        type: 'RECEIVE_PAYMENT_INFO',
        payload,
      });
    })
    .catch((error: Error) => {
      console.warn('fetch error', error);
      dispatch({
        type: 'REQUEST_PAYMENT_INFO_ERROR',
      });
    });
};

// POST /est/purchase
export const requestPurchase = (
  res: string,
  paymentId: number,
  planId: number,
  productId: number,
  title: string,
  contentId: number,
): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();
  const {
    auth: { viewer: { uuid } = {} },
  } = state;

  if (!uuid) return undefined;

  dispatch({
    type: 'REQUEST_PURCHASE',
  });
  const data = {
    payment_instrument_id: paymentId,
    product_id: productId,
    pricing_plan_id: planId,
    title,
    resolution: res,
    fingerprint: 'CLIENT_MUST_GENERATE',
  };

  const baseUrl = `${config.session.radish_url}est/purchase`;
  const url = appendParamsToQueryString(baseUrl, paramify(data));

  return postJson({ url, state })
    .then(() => {
      dispatch({
        type: 'PURCHASE_SUCCESS',
        contentId,
      });
    })
    .catch((error: Error) => {
      console.warn('PURCHASE ERROR', error);
      dispatch({
        type: 'PURCHASE_FAILURE',
      });
    });
};

// GET /est/purchases
export const fetchPurchases = (count?: number = -1): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();
  const {
    auth: { viewer: { uuid } = {} },
  } = state;

  if (!uuid) return undefined;

  dispatch({ type: 'REQUEST_PURCHASES' });

  const url = `${config.session.radish_url}est/purchases?sort=purchase_date`;

  const data = count > 0 ? { maxcount: count } : {};

  return getJson({ url, state, data })
    .then((payload: PurchaseInfo) => {
      dispatch({ type: 'RECEIVE_PURCHASES', payload });
    })
    .catch((error: Error) => {
      console.warn('fetch error', error);
      dispatch({ type: 'RECEIVE_PURCHASES_ERROR' });
    });
};

// POST /est/view_content
export const postViewContent = (
  { productId, pricingPlanId }: ViewContent,
  callback?: (error?: string) => void,
): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();
  const {
    auth: { viewer: { uuid } = {} },
  } = state;

  if (!uuid) return undefined;

  dispatch({
    type: 'REQUEST_EST_VIEW_CONTENT',
  });

  const data = {
    product_id: productId,
    pricing_plan_id: pricingPlanId,
  };
  const url = `${config.session.radish_url}est/view_content`;
  const options = {
    url,
    state,
    data,
  };

  if (!productId || !pricingPlanId) {
    return Promise.resolve();
  }

  return post(options)
    .then(response => {
      if (response.status === 200) {
        response.json().then(payload => {
          if (payload.fault) {
            dispatch({
              type: 'RECEIVE_EST_VIEW_CONTENT_ERROR',
            });
            callback?.(`Video heartbeat fault code: ${payload.fault.code}`);
          } else {
            dispatch({
              type: 'RECEIVE_EST_VIEW_CONTENT',
              payload,
            });
            callback?.();
          }
        });
      } else {
        dispatch({
          type: 'RECEIVE_EST_VIEW_CONTENT_ERROR',
        });
        callback?.(`Video heartbeat HTTP error: ${response.status}`);
      }
    })
    .catch(error => {
      console.warn(error);
      dispatch({
        type: 'RECEIVE_EST_VIEW_CONTENT_ERROR',
      });
      callback?.(`Video heartbeat error: ${error.message}`);
    });
};

// POST /est/update_content_progress
export const updateContentProgress = (
  {
    productId,
    pricingPlanId,
    progressSeconds,
    viewContentReference,
    streamingComplete,
    viewingComplete,
  }: ViewContent,
  callback: () => void,
): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();
  const {
    auth: { viewer: { uuid } = {} },
  } = state;

  if (!uuid) return undefined;

  if (!productId || !pricingPlanId) {
    return Promise.reject(new Error('Invalid productId or pricingPlanId'));
  }

  dispatch({
    type: 'REQUEST_UPDATE_CONTENT_PROGRESS',
  });

  const data = {
    product_id: productId,
    pricing_plan_id: pricingPlanId,
    progress_seconds: progressSeconds,
    view_content_reference: viewContentReference,
    streaming_complete: streamingComplete, // content ended,
    viewing_complete: viewingComplete, // session ended,
  };

  const baseUrl = `${config.session.radish_url}est/update_content_progress`;
  const url = appendParamsToQueryString(baseUrl, paramify(data));

  return postJson({ url, state, data })
    .then(payload => {
      if (payload?.fault?.code === 1050 && callback) {
        callback();
      }

      dispatch({
        type: 'RECEIVE_UPDATE_CONTENT_PROGRESS',
        payload,
      });
    })
    .catch(error => {
      dispatch({
        type: 'RECEIVE_UPDATE_CONTENT_PROGRESS_ERROR',
      });
      console.warn('RECEIVE_UPDATE_CONTENT_PROGRESS_ERROR fetch error', error);
    });
};

export const resetFilters = () => ({
  type: 'RESET_FILTERS',
});

export const updateReducerFilters = newFilters => ({
  type: 'UPDATE_FILTERS',
  payload: newFilters,
});

export const resetSort = () => ({
  type: 'RESET_SORT',
});

export const updateReducerSort = newSort => ({
  type: 'UPDATE_SORT',
  payload: newSort,
});

// ****INTERNAL ACTIONS**** //
export const selectCard = (card: Card): ThunkAction => ({ dispatch }): void => {
  dispatch({
    type: 'SELECT_CARD',
    payload: card,
  });
};

export const clearErrorMessage = () => ({ type: 'CLEAR_REMOVE_EST_DEVICE_ERROR_MESSAGE' });
